import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Add, Close } from "@mui/icons-material";
import { userSelector } from "src/store/slices/userSlice";
import {
  getAllOrganisations,
  inviteUsers,
  organisationSelector,
} from "src/store/slices/organisationSlice";
import { StepMessage } from "src/components/Auth/common";
import regex from "src/utils/regex";
import readableABNString from "src/utils/readableABNString";
import {
  invitableUserTypes,
  invitationLinkDays,
  invitationMessages,
  maxInvites,
  organisationStatus,
  userTypeLabels,
  userTypes,
} from "src/constants";

const titleId = "invite-users-title";

const normaliseEmail = (email) => email.trim().toLowerCase();

const peopleCount = (count) => `${count} ${count === 1 ? "person" : "people"}`;

// Keep in sync with isInvitableOrganisation on the server
const uninvitableStatuses = [organisationStatus.PENDING_APPROVAL, organisationStatus.DEACTIVATED];

// The organisations a master user can invite to: approved ones that aren't deactivated, by name
const invitableOrganisations = (organisations = []) =>
  organisations
    .filter(({ status }) => !uninvitableStatuses.includes(status))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

function firstNameError(firstName) {
  if (!firstName.trim()) return invitationMessages.FIRST_NAME_REQUIRED;
  if (!regex.name.test(firstName.trim())) return invitationMessages.NAME_INVALID;

  return "";
}

function emailError(rows, index) {
  const email = normaliseEmail(rows[index].email);

  if (!email) return invitationMessages.EMAIL_REQUIRED;
  if (!regex.email.test(email)) return invitationMessages.EMAIL_INVALID;
  // Only on the repeats, so the first one stays valid
  if (rows.findIndex((row) => normaliseEmail(row.email) === email) !== index)
    return invitationMessages.EMAIL_DUPLICATE;

  return "";
}

// Once sent: who was invited (and as what, when the inviter could choose), and who wasn't and why (e.g. they already
// have an account)
function InviteResult({ result: { invited, skipped }, showUserType, onInviteMore, onDone }) {
  return (
    <>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          {invited.length
            ? `Invitations sent to ${peopleCount(invited.length)}. Each link can be used once and expires in ${invitationLinkDays} days.`
            : "No invitations were sent."}
        </Typography>

        {invited.map(({ firstName, email, userType }) => (
          <Typography key={email} variant="body2" sx={{ mb: 0.5 }}>
            {firstName} · {email}
            {showUserType && ` · ${userTypeLabels[userType]}`}
          </Typography>
        ))}

        {skipped.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              Not invited
            </Typography>
            {skipped.map(({ email, reason }) => (
              <Typography key={email} variant="body2" sx={{ mb: 0.5 }}>
                {email} · {reason}
              </Typography>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onInviteMore}>Invite more people</Button>
        <Button variant="contained" color="secondary" onClick={onDone}>
          Done
        </Button>
      </DialogActions>
    </>
  );
}

// The users page's "Invite users" button and dialog, for master users and admins: several people are invited at once
// to register in an organisation. Admins invite users to their own organisation; master users pick the organisation
// and invite each person as a user or an admin.
export default function InviteUsers() {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const { organisations, gettingOrganisations } = useSelector(organisationSelector);
  const isMaster = user.userType === userTypes.MASTER;
  // Admins only invite users, so they have nothing to pick
  const userTypeOptions = invitableUserTypes[user.userType] || [];
  const canPickUserType = userTypeOptions.length > 1;

  const nextRowKey = useRef(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [organisation, setOrganisation] = useState(null);
  const [organisationsError, setOrganisationsError] = useState("");
  // Errors of fields not yet left are only shown once sending is tried
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // The server's answer once sent: { invited, skipped }
  const [result, setResult] = useState(null);

  const createRow = () => ({
    key: nextRowKey.current++,
    firstName: "",
    email: "",
    userType: userTypes.USER,
    touched: {},
  });

  const errors = rows.map((row, index) => ({
    firstName: firstNameError(row.firstName),
    email: emailError(rows, index),
  }));
  const hasRowErrors = errors.some((rowErrors) => rowErrors.firstName || rowErrors.email);
  const organisationError = isMaster && !organisation ? invitationMessages.ORGANISATION_REQUIRED : "";

  const shownError = (row, field) =>
    showAllErrors || row.touched[field] ? errors[rows.indexOf(row)][field] : "";

  function resetForm() {
    setRows([createRow()]);
    setShowAllErrors(false);
    setSubmitError("");
    setResult(null);
  }

  async function handleOpen() {
    resetForm();
    setOrganisation(null);
    setOpen(true);

    if (!isMaster) return;

    setOrganisationsError("");
    const loaded = await dispatch(getAllOrganisations());
    if (getAllOrganisations.rejected.match(loaded)) setOrganisationsError(invitationMessages.ORGANISATIONS_FAILED);
  }

  function handleClose() {
    if (!isSending) setOpen(false);
  }

  function updateRow(key, changes) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));
  }

  function touchField(key, field) {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, touched: { ...row.touched, [field]: true } } : row))
    );
  }

  function addRow() {
    setRows((current) => [...current, createRow()]);
  }

  function removeRow(key) {
    setRows((current) => current.filter((row) => row.key !== key));
  }

  async function handleSend() {
    setShowAllErrors(true);
    if (hasRowErrors || organisationError) return;

    setSubmitError("");
    setIsSending(true);

    const sent = await dispatch(
      inviteUsers({
        organisationId: isMaster ? organisation._id : undefined,
        invites: rows.map(({ firstName, email, userType }) => ({
          firstName: firstName.trim(),
          email: email.trim(),
          userType,
        })),
      })
    );

    setIsSending(false);

    if (inviteUsers.fulfilled.match(sent)) return setResult(sent.payload.data);

    const { message } = sent.payload || {};
    setSubmitError(typeof message === "string" ? message : invitationMessages.GENERIC_ERROR);
  }

  const shownOrganisationError = showAllErrors ? organisationError : "";

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        size="small"
        onClick={handleOpen}
        style={{ marginRight: "30px" }}
      >
        Invite users
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" aria-labelledby={titleId}>
        <DialogTitle id={titleId} sx={{ fontWeight: 700 }}>
          Invite users
        </DialogTitle>

        {result ? (
          <InviteResult
            result={result}
            showUserType={canPickUserType}
            onInviteMore={resetForm}
            onDone={handleClose}
          />
        ) : (
          <>
            <DialogContent>
              <Typography sx={{ mb: 3, color: "text.secondary" }}>
                Each person gets an email with a link to create their account
                {isMaster ? "" : ` in ${user.organisation?.name}`}. Their account is active as soon as they
                register. The link can be used once and expires in {invitationLinkDays} days.
              </Typography>

              {isMaster && (
                <Autocomplete
                  options={invitableOrganisations(organisations)}
                  value={organisation}
                  onChange={(event, value) => setOrganisation(value)}
                  loading={gettingOrganisations}
                  disabled={isSending}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ABN {readableABNString(option.abn)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label="Organisation"
                      placeholder="Search by name"
                      error={Boolean(shownOrganisationError)}
                      helperText={shownOrganisationError}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {gettingOrganisations && <CircularProgress color="inherit" size={18} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 3 }}
                />
              )}

              {rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: canPickUserType ? "1fr 1.5fr 130px 40px" : "1fr 1.5fr 40px",
                    gap: 1.5,
                    alignItems: "start",
                    mb: 2,
                  }}
                >
                  <TextField
                    required
                    size="small"
                    label="First name"
                    value={row.firstName}
                    disabled={isSending}
                    inputProps={{ maxLength: 24 }}
                    onChange={(event) => updateRow(row.key, { firstName: event.target.value })}
                    onBlur={() => touchField(row.key, "firstName")}
                    error={Boolean(shownError(row, "firstName"))}
                    helperText={shownError(row, "firstName")}
                  />
                  <TextField
                    required
                    size="small"
                    type="email"
                    label="Email"
                    value={row.email}
                    disabled={isSending}
                    inputProps={{ maxLength: 50 }}
                    onChange={(event) => updateRow(row.key, { email: event.target.value })}
                    onBlur={() => touchField(row.key, "email")}
                    error={Boolean(shownError(row, "email"))}
                    helperText={shownError(row, "email")}
                  />
                  {canPickUserType && (
                    <TextField
                      select
                      size="small"
                      label="User type"
                      value={row.userType}
                      disabled={isSending}
                      onChange={(event) => updateRow(row.key, { userType: event.target.value })}
                    >
                      {userTypeOptions.map((userType) => (
                        <MenuItem key={userType} value={userType}>
                          {userTypeLabels[userType]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <IconButton
                    aria-label={`Remove ${row.email.trim() || "this person"}`}
                    onClick={() => removeRow(row.key)}
                    disabled={rows.length === 1 || isSending}
                  >
                    <Close />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<Add />}
                onClick={addRow}
                disabled={rows.length >= maxInvites || isSending}
                sx={{ textTransform: "none" }}
              >
                Add another person
              </Button>
              {rows.length >= maxInvites && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  You can invite up to {maxInvites} people at a time.
                </Typography>
              )}

              <StepMessage error={submitError || organisationsError} />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSending}>
                Cancel
              </Button>
              <LoadingButton variant="contained" color="secondary" loading={isSending} onClick={handleSend}>
                Send {rows.length > 1 ? `${rows.length} invitations` : "invitation"}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
